import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  Wallet,
  Gamepad2,
  Trophy,
  Gift,
  LifeBuoy,
  Bell,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Shield,
  FileText,
  Activity,
  Zap,
} from 'lucide-react';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/lib/utils';

interface NavItem {
  label: string;
  icon: React.ElementType;
  href?: string;
  children?: { label: string; href: string }[];
  badge?: number;
}

const navigation: NavItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, href: '/' },
  { label: 'Analytics', icon: BarChart3, href: '/analytics' },
  { label: 'Real-Time', icon: Activity, href: '/real-time' },
  {
    label: 'Users',
    icon: Users,
    children: [
      { label: 'All Users', href: '/users' },
      { label: 'Banned Users', href: '/users/banned' },
      { label: 'User Activity', href: '/users/activity' },
    ],
  },
  {
    label: 'KYC',
    icon: Shield,
    children: [
      { label: 'Applications', href: '/kyc' },
      { label: 'Settings', href: '/kyc/settings' },
    ],
  },
  {
    label: 'Financial',
    icon: Wallet,
    children: [
      { label: 'Transactions', href: '/transactions' },
      { label: 'Deposits', href: '/deposits' },
      { label: 'Withdrawals', href: '/withdrawals' },
      { label: 'QR Management', href: '/qr-management' },
      { label: 'Wallet Overview', href: '/wallet' },
      { label: 'Payment Settings', href: '/payments/settings' },
    ],
  },
  {
    label: 'Games',
    icon: Gamepad2,
    children: [
      { label: 'All Rooms', href: '/games' },
      { label: 'Ludo Games', href: '/games/ludo' },
      { label: 'Disputes', href: '/games/disputes' },
      { label: 'Game Settings', href: '/games/settings' },
    ],
  },
  { label: 'Leaderboard', icon: Trophy, href: '/leaderboard' },
  {
    label: 'Marketing',
    icon: Gift,
    children: [
      { label: 'Referrals', href: '/referrals' },
      { label: 'Promotions', href: '/promotions' },
      { label: 'Bonus Management', href: '/bonus' },
    ],
  },
  {
    label: 'Support',
    icon: LifeBuoy,
    children: [
      { label: 'Tickets', href: '/support' },
      { label: 'FAQ Management', href: '/support/faq' },
    ],
  },
  {
    label: 'Notifications',
    icon: Bell,
    children: [
      { label: 'All Notifications', href: '/notifications' },
      { label: 'Send New', href: '/notifications/send' },
      { label: 'Templates', href: '/notifications/templates' },
    ],
  },
  {
    label: 'Reports',
    icon: FileText,
    children: [
      { label: 'Revenue', href: '/reports/revenue' },
      { label: 'Users', href: '/reports/users' },
      { label: 'Games', href: '/reports/games' },
      { label: 'Financial', href: '/reports/financial' },
      { label: 'Custom', href: '/reports/custom' },
    ],
  },
  {
    label: 'Settings',
    icon: Settings,
    children: [
      { label: 'General', href: '/settings' },
      { label: 'Security', href: '/settings/security' },
      { label: 'Admin Users', href: '/settings/admins' },
      { label: 'System Health', href: '/settings/health' },
    ],
  },
];

export default function Sidebar() {
  const location = useLocation();
  const { sidebarCollapsed, sidebarMobileOpen, setSidebarCollapsed, setSidebarMobileOpen } = useUIStore();
  const { admin, logout } = useAuthStore();

  const isActiveRoute = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarCollapsed ? 80 : 288 }}
        className={cn(
          'fixed top-0 left-0 h-screen bg-slate-900/95 backdrop-blur-xl border-r border-slate-800/50 z-50',
          'hidden lg:flex flex-col',
          'shadow-xl shadow-black/20'
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800/50">
          <AnimatePresence mode="wait">
            {!sidebarCollapsed ? (
              <motion.div
                key="full-logo"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-lg gradient-text">GameZone</h1>
                  <p className="text-xs text-slate-500">Admin Panel</p>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="icon-logo"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-10 h-10 mx-auto rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/30"
              >
                <Zap className="w-6 h-6 text-white" />
              </motion.div>
            )}
          </AnimatePresence>

          {!sidebarCollapsed && (
            <button
              onClick={() => setSidebarCollapsed(true)}
              className="p-2 rounded-lg hover:bg-slate-800/50 text-slate-400 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto custom-scrollbar py-4 px-3">
          <ul className="space-y-1">
            {navigation.map((item) => (
              <NavItemComponent
                key={item.label}
                item={item}
                collapsed={sidebarCollapsed}
                isActive={item.href ? isActiveRoute(item.href) : item.children?.some((c) => isActiveRoute(c.href)) ?? false}
              />
            ))}
          </ul>
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-800/50">
          <AnimatePresence mode="wait">
            {!sidebarCollapsed ? (
              <motion.div
                key="full-user"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {admin?.name?.charAt(0) ?? 'A'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{admin?.name ?? 'Admin'}</p>
                  <p className="text-xs text-slate-500 truncate">{admin?.role ?? 'admin'}</p>
                </div>
                <button
                  onClick={logout}
                  className="p-2 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400 transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </motion.div>
            ) : (
              <motion.button
                key="icon-logout"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={logout}
                className="w-10 h-10 mx-auto rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400 transition-colors flex items-center justify-center"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        {/* Expand button (when collapsed) */}
        {sidebarCollapsed && (
          <button
            onClick={() => setSidebarCollapsed(false)}
            className="absolute top-20 -right-3 w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white hover:bg-violet-600 transition-all shadow-lg"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </motion.aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {sidebarMobileOpen && (
          <motion.aside
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed top-0 left-0 h-screen w-72 bg-slate-900/95 backdrop-blur-xl border-r border-slate-800/50 z-50 lg:hidden flex flex-col"
          >
            {/* Same content as desktop but always expanded */}
            <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-lg gradient-text">GameZone</h1>
                  <p className="text-xs text-slate-500">Admin Panel</p>
                </div>
              </div>
              <button
                onClick={() => setSidebarMobileOpen(false)}
                className="p-2 rounded-lg hover:bg-slate-800/50 text-slate-400"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto custom-scrollbar py-4 px-3">
              <ul className="space-y-1">
                {navigation.map((item) => (
                  <NavItemComponent
                    key={item.label}
                    item={item}
                    collapsed={false}
                    isActive={item.href ? isActiveRoute(item.href) : item.children?.some((c) => isActiveRoute(c.href)) ?? false}
                    onNavigate={() => setSidebarMobileOpen(false)}
                  />
                ))}
              </ul>
            </nav>

            <div className="p-4 border-t border-slate-800/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {admin?.name?.charAt(0) ?? 'A'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{admin?.name ?? 'Admin'}</p>
                  <p className="text-xs text-slate-500 truncate">{admin?.role ?? 'admin'}</p>
                </div>
                <button
                  onClick={logout}
                  className="p-2 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}

interface NavItemComponentProps {
  item: NavItem;
  collapsed: boolean;
  isActive: boolean;
  onNavigate?: () => void;
}

function NavItemComponent({ item, collapsed, isActive, onNavigate }: NavItemComponentProps) {
  const location = useLocation();
  const [isOpen, setIsOpen] = React.useState(isActive);

  // Simple link item
  if (item.href && !item.children) {
    return (
      <li>
        <Link
          to={item.href}
          onClick={onNavigate}
          className={cn(
            'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200',
            isActive
              ? 'bg-gradient-to-r from-violet-600/20 to-indigo-600/20 text-white border-l-4 border-violet-500 -ml-0.5'
              : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
          )}
          title={collapsed ? item.label : undefined}
        >
          <item.icon className={cn('w-5 h-5 flex-shrink-0', isActive && 'text-violet-400')} />
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                className="font-medium text-sm"
              >
                {item.label}
              </motion.span>
            )}
          </AnimatePresence>
          {item.badge && !collapsed && (
            <span className="ml-auto px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-medium">
              {item.badge}
            </span>
          )}
        </Link>
      </li>
    );
  }

  // Expandable item with children
  return (
    <li>
      <button
        onClick={() => !collapsed && setIsOpen(!isOpen)}
        className={cn(
          'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200',
          isActive
            ? 'bg-gradient-to-r from-violet-600/20 to-indigo-600/20 text-white'
            : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
        )}
        title={collapsed ? item.label : undefined}
      >
        <item.icon className={cn('w-5 h-5 flex-shrink-0', isActive && 'text-violet-400')} />
        <AnimatePresence>
          {!collapsed && (
            <>
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="font-medium text-sm flex-1 text-left"
              >
                {item.label}
              </motion.span>
              <motion.div
                animate={{ rotate: isOpen ? 90 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronRight className="w-4 h-4" />
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {isOpen && !collapsed && item.children && (
          <motion.ul
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden ml-4 mt-1 space-y-1 border-l border-slate-800"
          >
            {item.children.map((child) => (
              <li key={child.href}>
                <Link
                  to={child.href}
                  onClick={onNavigate}
                  className={cn(
                    'block px-4 py-2 text-sm rounded-r-lg transition-colors',
                    location.pathname === child.href
                      ? 'text-violet-400 bg-violet-500/10 border-l-2 border-violet-500 -ml-px'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/30'
                  )}
                >
                  {child.label}
                </Link>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </li>
  );
}

import React from 'react';
